import React, { useState, useEffect } from 'react';
import {
  CButton,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CProgress,
} from '@coreui/react';
import { CChartPie } from '@coreui/react-chartjs';
import { pieChartOptions } from '../../../utils/chartTheme';
import axios from 'axios';
import Papa from 'papaparse';
import CIcon from '@coreui/icons-react';
import { cilTrash } from '@coreui/icons';
import '../../../scss/main.scss';
import config from '../../../utils/EnvironmentSwitcher';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const fhirEndpoint = config.fhirUrl || 'https://dev-fhir.mieros.cloud/fhir';

const ExpensePieChart = ({ authUser, fhirToken }) => {
  const [chartDataFHIR, setChartDataFHIR] = useState(null);
  const [expenseDetailsFHIR, setExpenseDetailsFHIR] = useState([]);
  const [chartDataCSV, setChartDataCSV] = useState(null);
  const [expenseDetailsCSV, setExpenseDetailsCSV] = useState([]);
  const [csvError, setCsvError] = useState(null);
  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState('fhir');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [csvSource, setCsvSource] = useState('Capital One'); // Default source
  const [authError, setAuthError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const toggleModal = () => setModal(!modal);

  const processFHIRData = (entries) => {
    const expenseData = {};
    const expenseDetails = [];

    entries.forEach((entry) => {
      const resource = entry.resource;
      const description = resource?.code?.text || 'Unknown';
      const amount = resource?.quantity?.value || 0;
      const dateField = resource?.enteredDate || resource?.meta?.lastUpdated;
      const date = dateField
        ? new Date(dateField).toISOString()
        : 'Unknown Date';
      const id = resource?.id;

      // Log the processing of each ChargeItem
      //console.log(`Processing ChargeItem: ${description}, Amount: ${amount}, Date: ${date}, ID: ${id}`);

      // Aggregate amounts for the pie chart
      if (!expenseData[description]) {
        expenseData[description] = 0;
      }
      expenseData[description] += amount;

      // Collect detailed expense data for the table
      expenseDetails.push({
        id,
        description,
        amount,
        date,
      });
    });

    // Sort and slice for top 10 expenses
    const sortedExpenses = Object.entries(expenseData)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    const categories = sortedExpenses.map(([category]) => category);
    const amounts = sortedExpenses.map(([, amount]) => amount);

    setChartDataFHIR({
      labels: categories,
      datasets: [
        {
          label: 'Top 10 Expenses (FHIR)',
          data: amounts,
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#FF9F40',
            '#4BC0C0',
            '#9966FF',
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#FF9F40',
          ],
        },
      ],
    });

    // Set top 10 expense details
    const topExpenseDetails = expenseDetails
      .filter((expense) => categories.includes(expense.description))
      .sort((a, b) => b.amount - a.amount);

    setExpenseDetailsFHIR(topExpenseDetails);
  };

    // Function to handle deleting an expense
    const handleDeleteExpense = async (id) => {
      if (!window.confirm('Are you sure you want to delete this expense?')) {
        return;
      }

  
      try {
        await axios.delete(`${fhirEndpoint}/fhir/ChargeItem/${id}`, {
          headers: {
            'Content-Type': 'application/fhir+json',
            Authorization: `Bearer ${authUser.authUser.stsTokenManager.accessToken}`,
          },
        });
  
        alert('Expense deleted successfully.');
        fetchChargeItems(); // Refresh data after deletion
      } catch (error) {
        console.error('Error deleting ChargeItem:', error);
        alert('Failed to delete expense.');
      }
    };
  

  const fetchChargeItems = async () => {
    try {
      setAuthError(null); // Clear any previous auth errors
      let allEntries = [];
      let nextUrl = `${fhirEndpoint}/fhir/ChargeItem?identifier=${encodeURIComponent(
        `https://fhir.mieros.cloud/fhir/ids|${authUser.authUser.uid}`
      )}&_count=200`;
      let pageCount = 0;
      const visitedUrls = new Set();
      const MAX_PAGES = 25;
     // console.log('Fetching URL:', nextUrl);
      while (nextUrl) {
        if (visitedUrls.has(nextUrl) || pageCount >= MAX_PAGES) {
          break;
        }
        visitedUrls.add(nextUrl);
        pageCount += 1;

         // Ensure nextUrl is always HTTPS
      nextUrl = nextUrl.replace(/^http:/, 'https:');
      //console.log('Fetching URL:', nextUrl);

        const response = await axios.get(nextUrl, {
          headers: {
            'Content-Type': 'application/fhir+json',
            Authorization: `Bearer ${authUser.authUser.stsTokenManager.accessToken}`,
          },
        });
        const bundle = response.data;

        if (bundle.entry && bundle.entry.length > 0) {
          allEntries = allEntries.concat(bundle.entry);
        }

        const nextLink = bundle.link?.find((link) => link.relation === 'next');
        nextUrl = nextLink ? nextLink.url : null;
      }

      if (allEntries.length > 0) {
        processFHIRData(allEntries);
      } else {
        setChartDataFHIR(null);
        setExpenseDetailsFHIR([]);
      }
    } catch (error) {
      console.error('Error fetching ChargeItem data:', error);
      if (error.response?.status === 401) {
        setAuthError('Your session has expired. Please log in again.');
      }
    }
  };

  const exportToFHIR = async () => {
    if (!chartDataCSV) return;

    try {
      setAuthError(null);
      setLoading(true);
      setProgress(0);
      setExportModalVisible(true);

      const totalItems = expenseDetailsCSV.length;
      let processedItems = 0;
      let errorCount = 0;

      for (let i = 0; i < totalItems; i++) {
        const { description, amount, date } = expenseDetailsCSV[i];
        const enteredDate = date !== 'Unknown Date' ? date : new Date().toISOString();

        try {
          // Check for existing ChargeItem
          const existingChargeItemUrl = `${fhirEndpoint}/fhir/ChargeItem?code:text=${encodeURIComponent(
            description
          )}&identifier=${encodeURIComponent(`https://fhir.mieros.cloud/fhir/ids|${authUser.authUser.uid}`)}`;

          const existingChargeItemResponse = await fetch(existingChargeItemUrl, {
            headers: {
              'Content-Type': 'application/fhir+json',
              Authorization: `Bearer ${authUser.authUser.stsTokenManager.accessToken}`,
            },
          });

          if (existingChargeItemResponse.status === 401) {
            setAuthError('Your session has expired. Please log in again.');
            setLoading(false);
            setExportModalVisible(false);
            return;
          }

          if (!existingChargeItemResponse.ok) {
            throw new Error(`HTTP error! status: ${existingChargeItemResponse.status}`);
          }

          const existingChargeItemData = await existingChargeItemResponse.json();

          let duplicateFound = false;

          if (existingChargeItemData.total > 0) {
            // Manually check for duplicates based on amount and date
            for (const entry of existingChargeItemData.entry) {
              const resource = entry.resource;
              const existingAmount = resource?.quantity?.value || 0;
              const existingDate = resource?.enteredDate || resource?.meta?.lastUpdated;

              if (
                existingAmount === amount &&
                existingDate === enteredDate
              ) {
                duplicateFound = true;
                console.log(
                  `Duplicate found for description "${description}". Skipping creation.`
                );
                break;
              }
            }
          }

          if (duplicateFound) {
            processedItems++;
            setProgress((processedItems / totalItems) * 100);
            continue;
          }

          // Check if ChargeItemDefinition exists
          const queryUrl = `${fhirEndpoint}/fhir/ChargeItemDefinition?description=${encodeURIComponent(
            description
          )}`;

          const existingResponse = await fetch(queryUrl, {
            headers: {
              'Content-Type': 'application/fhir+json',
              Authorization: `Bearer ${authUser.authUser.stsTokenManager.accessToken}`,
            },
          });

          const existingData = await existingResponse.json();

          if (existingData.total > 0) {
            console.log(
              `ChargeItemDefinition for description "${description}" already exists. Skipping creation of definition.`
            );
          } else {
            // Create ChargeItemDefinition
            const chargeItemDefinition = {
              resourceType: 'ChargeItemDefinition',
              status: 'active',
              code: {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/chargeitem-billcode',
                    code: 'expense-definition',
                    display: 'Expense Definition',
                  },
                ],
                text: `A definition for the expense: ${description}`,
              },
              description: description,
            };

            await axios.post(`${fhirEndpoint}/fhir/ChargeItemDefinition`, chargeItemDefinition, {
              headers: {
                'Content-Type': 'application/fhir+json',
                Authorization: `Bearer ${authUser.authUser.stsTokenManager.accessToken}`,
              },
            });
          }

          // Create the ChargeItem
          const chargeItem = {
            resourceType: 'ChargeItem',
            status: 'billable',
            account: description,
            enteredDate: enteredDate,
            code: {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/chargeitem-billcode',
                  code: 'expense',
                  display: 'Expense',
                },
              ],
              text: description,
            },
            quantity: { value: amount },
            identifier: [
              {
                system: 'https://fhir.mieros.cloud/fhir/ids',
                value: authUser.authUser.uid,
              },
            ],
          };

          const chargeItemResponse = await axios.post(`${fhirEndpoint}/fhir/ChargeItem`, chargeItem, {
            headers: {
              'Content-Type': 'application/fhir+json',
              Authorization: `Bearer ${authUser.authUser.stsTokenManager.accessToken}`,
            },
          });

          console.log('ChargeItem created:', chargeItemResponse.data);

          processedItems++;
          setProgress((processedItems / totalItems) * 100);
        } catch (error) {
          errorCount++;
          if (error.response?.status === 401 || error.status === 401) {
            setAuthError('Your session has expired. Please log in again.');
            setLoading(false);
            setExportModalVisible(false);
            return;
          }
          console.error(`Error processing description "${description}":`, error);
        } finally {
          processedItems++;
          setProgress((processedItems / totalItems) * 100);
        }
      }

      setLoading(false);
      setExportModalVisible(false);
      
      if (errorCount > 0) {
        alert(`Export completed with ${errorCount} errors. Some items may not have been exported.`);
      } else {
        alert('Data exported to FHIR successfully!');
      }
      
      fetchChargeItems();
    } catch (error) {
      console.error('Error exporting data to FHIR:', error);
      setLoading(false);
      setExportModalVisible(false);
      if (error.response?.status === 401) {
        setAuthError('Your session has expired. Please log in again.');
      } else {
        alert('Failed to export data to FHIR.');
      }
    }
  };

  useEffect(() => {
    //     console.log("AuthUser structure:", authUser);
    // console.log("authUser.uid:", authUser?.authUser.uid);
    // console.log("fhirToken:", fhirToken);
    if (authUser?.authUser?.uid && fhirToken) {
      fetchChargeItems();
    } else {
      console.log('Missing authUser or fhirToken in useEffect:', { authUser, fhirToken });
    }
  }, [authUser, fhirToken, loading]);

  const processCSVData = (data) => {
    let transactions;
    if (csvSource === 'American Express') {
      transactions = data.map((transaction) => ({
        description: transaction['Description'],
        date: transaction['Date'] ? new Date(transaction['Date']) : 'Unknown Date',
        amount: parseFloat(transaction['Amount']) || 0,
      }));
    } else if (csvSource === 'Capital One') {
      transactions = data.map((transaction) => ({
        description: transaction['Description'],
        date: transaction['Transaction Date']
          ? new Date(transaction['Transaction Date'])
          : 'Unknown Date',
        amount: parseFloat(transaction['Debit']) || 0,
      }));
    }

    const expenseData = transactions.reduce((acc, { description, amount }) => {
      if (amount > 0) acc[description] = (acc[description] || 0) + amount;
      return acc;
    }, {});

    const sortedDescriptions = Object.entries(expenseData)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    const categories = sortedDescriptions.map(([description]) => description);
    const amounts = sortedDescriptions.map(([, amount]) => amount);

    setChartDataCSV({
      labels: categories,
      datasets: [
        {
          label: `Top 10 Expenses (${csvSource})`,
          data: amounts,
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#FF9F40',
            '#4BC0C0',
            '#9966FF',
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#FF9F40',
          ],
        },
      ],
    });

    const expenseDetails = transactions
      .filter((t) => categories.includes(t.description) && t.amount > 0)
      .map((t) => ({
        description: t.description,
        amount: t.amount,
        date: t.date instanceof Date ? t.date.toISOString() : 'Unknown Date',
      }));
    // console.log("here", JSON.stringify(expenseDetails));
    setExpenseDetailsCSV(expenseDetails);
  };

  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          processCSVData(results.data);
        },
        error: (error) => {
          setCsvError('Error parsing CSV file.');
          console.error('Error parsing CSV file:', error);
        },
      });
      event.target.value = null;
    }
  };

  // Calculate pagination
  const indexOfLastExpense = currentPage * pageSize;
  const indexOfFirstExpense = indexOfLastExpense - pageSize;
  const currentExpenses = expenseDetailsFHIR.slice(indexOfFirstExpense, indexOfLastExpense);
  const totalPages = Math.ceil(expenseDetailsFHIR.length / pageSize);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  return (
    <CCard className="expense-pie-chart">
      {authError && (
        <div className="alert alert-danger" role="alert" style={{ margin: '10px' }}>
          {authError}
        </div>
      )}
      <CCardHeader>
        <CNav variant="tabs">
          <CNavItem>
            <CNavLink active={activeTab === 'fhir'} onClick={() => setActiveTab('fhir')}>
              MiE Data
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink active={activeTab === 'csv'} onClick={() => setActiveTab('csv')}>
              CSV Data
            </CNavLink>
          </CNavItem>
        </CNav>
      </CCardHeader>
      <CCardBody>
        <CTabContent>
          {/* FHIR Tab Pane */}
          <CTabPane visible={activeTab === 'fhir'}>
            <div className="expense-content">
              {/* Left side: Pie Chart and Top 10 Summary */}
              <div className="pie-chart-section">
                {/* Pie Chart with fixed height */}
                <div className="chart-container">
                  {chartDataFHIR ? (
                    <CChartPie 
                      data={chartDataFHIR}
                      options={pieChartOptions}
                    />
                  ) : (
                    <p>No expenses available.</p>
                  )}
                </div>
                
                {/* Top 10 Summary Table with scrollable container */}
                {chartDataFHIR && (
                  <div className="expense-summary">
                    <h4>Top 10 Expenses Summary</h4>
                    <div className="table-container">
                      <CTable striped hover responsive className="summary-table">
                        <CTableHead>
                          <CTableRow>
                            <CTableHeaderCell>Category</CTableHeaderCell>
                            <CTableHeaderCell>Total Amount</CTableHeaderCell>
                          </CTableRow>
                        </CTableHead>
                        <CTableBody>
                          {chartDataFHIR.labels.map((label, index) => (
                            <CTableRow key={index}>
                              <CTableDataCell>{label}</CTableDataCell>
                              <CTableDataCell>
                                {chartDataFHIR.datasets[0].data[index].toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: 'USD',
                                })}
                              </CTableDataCell>
                            </CTableRow>
                          ))}
                        </CTableBody>
                      </CTable>
                    </div>
                  </div>
                )}
              </div>

              {/* Detailed Transactions with better spacing for delete button */}
              <div className="expense-details">
                {expenseDetailsFHIR.length > 0 && (
                  <div>
                    <div className="details-header">
                      <h4>Detailed Transactions</h4>
                      <div className="pagination-controls">
                        <CDropdown>
                          <CDropdownToggle color="secondary" size="sm">
                            Show {pageSize} entries
                          </CDropdownToggle>
                          <CDropdownMenu>
                            {[10, 50, 100].map((size) => (
                              <CDropdownItem 
                                key={size}
                                onClick={() => handlePageSizeChange(size)}
                                active={pageSize === size}
                              >
                                {size} entries
                              </CDropdownItem>
                            ))}
                          </CDropdownMenu>
                        </CDropdown>
                      </div>
                    </div>

                    <div className="table-container">
                      <CTable striped hover responsive className="details-table">
                        <CTableHead>
                          <CTableRow>
                            <CTableHeaderCell>Description</CTableHeaderCell>
                            <CTableHeaderCell className="amount-column">Amount</CTableHeaderCell>
                            <CTableHeaderCell className="date-column">Date</CTableHeaderCell>
                            <CTableHeaderCell className="action-column">Actions</CTableHeaderCell>
                          </CTableRow>
                        </CTableHead>
                        <CTableBody>
                          {currentExpenses.map((expense, index) => (
                            <CTableRow key={index} className="expense-row">
                              <CTableDataCell className="description-cell">
                                <span className="mobile-label">Description:</span>
                                {expense.description}
                              </CTableDataCell>
                              <CTableDataCell className="amount-cell">
                                <span className="mobile-label">Amount:</span>
                                {expense.amount.toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: 'USD',
                                })}
                              </CTableDataCell>
                              <CTableDataCell className="date-cell">
                                <span className="mobile-label">Date:</span>
                                {expense.date !== 'Unknown Date'
                                  ? new Date(expense.date).toLocaleDateString()
                                  : 'Unknown Date'}
                              </CTableDataCell>
                              <CTableDataCell className="action-column">
                                <CButton
                                  color="danger"
                                  variant="outline"
                                  size="sm"
                                  className="delete-button"
                                  onClick={() => handleDeleteExpense(expense.id)}
                                >
                                  <CIcon icon={cilTrash} />
                                </CButton>
                              </CTableDataCell>
                            </CTableRow>
                          ))}
                        </CTableBody>
                      </CTable>
                    </div>

                    {/* Pagination */}
                    <div className="pagination-footer">
                      <div className="pagination-info">
                        Showing {indexOfFirstExpense + 1} to {Math.min(indexOfLastExpense, expenseDetailsFHIR.length)} of {expenseDetailsFHIR.length} entries
                      </div>
                      <div className="pagination-buttons">
                        <CButton
                          color="secondary"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </CButton>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                          <CButton
                            key={number}
                            color={currentPage === number ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => handlePageChange(number)}
                            className="page-number"
                          >
                            {number}
                          </CButton>
                        ))}
                        <CButton
                          color="secondary"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </CButton>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* View Larger Button */}
            {chartDataFHIR && (
              <div className="view-larger-container">
                <CButton
                  color="primary"
                  size="sm"
                  onClick={toggleModal}
                  className="view-larger-button"
                >
                  View Larger
                </CButton>
              </div>
            )}
          </CTabPane>
          <CTabPane visible={activeTab === 'csv'}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <div className="mb-2">Top 10 Expenses from CSV</div>
                {/* Dropdown for selecting source */}
                <CDropdown className="mb-3">
                  <CDropdownToggle color="secondary">
                    {csvSource}
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem onClick={() => setCsvSource('Capital One')}>Capital One</CDropdownItem>
                    <CDropdownItem onClick={() => setCsvSource('American Express')}>American Express</CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
                <CButton color="primary" onClick={() => document.getElementById('csvInput').click()}>
                  Upload CSV
                </CButton>
                <input
                  type="file"
                  id="csvInput"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleCSVUpload}
                />
              </div>
              {chartDataCSV && (
                <CButton
                  color="success"
                  onClick={exportToFHIR}
                >
                  Export to FHIR
                </CButton>
              )}
            </div>

            {csvError && <p style={{ color: 'red' }}>{csvError}</p>}
            
            <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
              {/* Left side: Pie Chart and Top 10 Summary */}
              <div style={{ flex: '1' }}>
                {/* Pie Chart */}
                <div style={{ marginBottom: '20px' }}>
                  {chartDataCSV ? (
                    <CChartPie data={chartDataCSV} options={pieChartOptions} width={200} height={200} />
                  ) : (
                    <p>No CSV data available.</p>
                  )}
                </div>
                {/* Top 10 Summary Table */}
                {chartDataCSV && (
                  <div>
                    <h4>Top 10 Expenses Summary</h4>
                    <CTable striped hover responsive>
                      <CTableHead>
                        <CTableRow>
                          <CTableHeaderCell>Category</CTableHeaderCell>
                          <CTableHeaderCell>Total Amount</CTableHeaderCell>
                        </CTableRow>
                      </CTableHead>
                      <CTableBody>
                        {chartDataCSV.labels.map((label, index) => (
                          <CTableRow key={index}>
                            <CTableDataCell>{label}</CTableDataCell>
                            <CTableDataCell>
                              {chartDataCSV.datasets[0].data[index].toLocaleString(undefined, {
                                style: 'currency',
                                currency: 'USD',
                              })}
                            </CTableDataCell>
                          </CTableRow>
                        ))}
                      </CTableBody>
                    </CTable>
                  </div>
                )}
              </div>

              {/* Right side: Detailed Transactions */}
              <div style={{ flex: '1' }}>
                {expenseDetailsCSV.length > 0 && (
                  <div>
                    <h4>Detailed Transactions</h4>
                    <CTable striped hover responsive>
                      <CTableHead>
                        <CTableRow>
                          <CTableHeaderCell>Description</CTableHeaderCell>
                          <CTableHeaderCell>Amount</CTableHeaderCell>
                          <CTableHeaderCell>Date</CTableHeaderCell>
                        </CTableRow>
                      </CTableHead>
                      <CTableBody>
                        {expenseDetailsCSV.map((expense, index) => (
                          <CTableRow key={index}>
                            <CTableDataCell>{expense.description}</CTableDataCell>
                            <CTableDataCell>
                              {expense.amount.toLocaleString(undefined, {
                                style: 'currency',
                                currency: 'USD',
                              })}
                            </CTableDataCell>
                            <CTableDataCell>
                              {expense.date !== 'Unknown Date'
                                ? new Date(expense.date).toLocaleDateString()
                                : 'Unknown Date'}
                            </CTableDataCell>
                          </CTableRow>
                        ))}
                      </CTableBody>
                    </CTable>
                  </div>
                )}
              </div>
            </div>

            {/* View Larger Button */}
            {chartDataCSV && (
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <CButton
                  color="primary"
                  size="sm"
                  onClick={toggleModal}
                  style={{ fontSize: '10px', padding: '2px 6px' }}
                >
                  View Larger
                </CButton>
              </div>
            )}
          </CTabPane>
        </CTabContent>
      </CCardBody>
      {/* Modal for Larger View */}
      <CModal visible={modal} onClose={() => setModal(false)} size="lg">
        <CModalHeader closeButton>Pie Chart - Larger View</CModalHeader>
        <CModalBody>
          {activeTab === 'fhir' && chartDataFHIR && (
            <CChartPie data={chartDataFHIR} options={pieChartOptions} width={400} height={400} />
          )}
          {activeTab === 'csv' && chartDataCSV && (
            <CChartPie data={chartDataCSV} options={pieChartOptions} width={400} height={400} />
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" size="sm" onClick={toggleModal}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>
      {/* Export Progress Modal */}
      <CModal visible={exportModalVisible} onClose={() => setExportModalVisible(false)} size="sm">
        <CModalHeader>Exporting Data to FHIR</CModalHeader>
        <CModalBody>
          <p>Exporting data, please wait...</p>
          <CProgress value={progress} color="success" className="mb-3" />
          <p>{Math.round(progress)}% completed</p>
        </CModalBody>
      </CModal>
    </CCard>
  );
};

export default ExpensePieChart;


